import { useGroupStorage } from '../../../../../../AppClients';
import { accountEntityName } from '../../../../../../backend/features/accounts/shared';
import { getDefaultOrgRole } from '../../../../../../backend/features/organizations/shared';
import { allowCreateGroup, allowOrgMembersEdit, getAccountsManageFilter, requireAnySystemPermission, } from '../../../../../../backend/features/permissions/shared';
import { useUserState } from '../../../../../../ui/hooks';
import { useInvalidateState } from '../../../../../../ui/hooks/deprecated/useInvalidateState';
import { useContactManagementTranslation } from '../../../../../../ui/hooks/useContactManagementTranslation';
import { ContactManagementConfigContext } from '../../../../../../ui/providers';
import { beginExport } from '@juvare/platform-sdk-react/ui/components/EntityTable/entityExport';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useExportClient } from '@juvare/platform-sdk-react/ui/hooks';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import SocialDistanceIcon from '@mui/icons-material/SocialDistance';
import React from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { showAccountsIManageFilterName } from '../../../../accounts/components/AccountsIManageSwitch';
import { showChangeOrganizationMemberRoleDialog } from '../components/OrganizationMemberRoleDialog';
import { showChangeOrganizationMemberStatusDialog } from '../components/OrganizationMemberStatusDialog';
import { handleAddMembersToOrganization, handleAddToExistingGroup, handleAddToNewGroup, handleExportAccounts, handleRemoveMembersFromOrganization, } from '../utils/AccountTableActionHandlers';
export function useOrganizationMembersTableActions({ dataTableProps, appliedFilterData, rowCount, organizationId, onReloadOrganization, onReloadTable, }) {
    var _a, _b, _c, _d, _e;
    const { t } = useContactManagementTranslation();
    const groupStorageClient = useGroupStorage();
    const exportClient = useExportClient();
    const userState = useUserState();
    const { accountsTableCustomization } = React.useContext(ContactManagementConfigContext);
    const settings = useSelector(getSettings);
    const { orgRoleMap, accountsExportEnabled, accountsExportFormat } = settings;
    const { invalidateState } = useInvalidateState();
    const [searchParams] = useSearchParams();
    const hasManageMembersPermission = allowOrgMembersEdit(userState);
    const hasExportPermission = requireAnySystemPermission(userState, 'export-accounts') && accountsExportEnabled;
    const location = {
        name: 'org-members',
        organizationId: organizationId,
    };
    const globalActions = [];
    const localActions = [];
    if (accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.getDataTableProps) {
        const tableProps = accountsTableCustomization.getDataTableProps(dataTableProps, appliedFilterData, location);
        globalActions.push(...((_a = tableProps.globalActions) !== null && _a !== void 0 ? _a : []));
        localActions.push(...((_b = tableProps.localActions) !== null && _b !== void 0 ? _b : []));
    }
    function handleExportAll() {
        var _a, _b;
        beginExport(exportClient, accountEntityName, accountsExportFormat !== null && accountsExportFormat !== void 0 ? accountsExportFormat : 'csv', undefined, [
            {
                column: 'orgStatus',
                op: 'overlap',
                value: Object.entries(settings.orgStatusMap).map(([value]) => `${organizationId}_${value}`),
            },
            { column: 'systemStatus', op: '!=', value: 'deleted' },
        ], (_b = (_a = dataTableProps.customBackendResponseData) === null || _a === void 0 ? void 0 : _a.storageClientRequest) === null || _b === void 0 ? void 0 : _b.orderBy);
    }
    if (hasExportPermission) {
        globalActions.push({
            icon: React.createElement(FileUploadOutlinedIcon, null),
            label: t('panels.accounts.exportAll'),
            onClick: handleExportAll,
            'data-test': 'export-all-button',
        });
    }
    if (hasManageMembersPermission) {
        globalActions.push({
            label: t('forms.orgMembers.addAccounts'),
            icon: React.createElement(AddIcon, null),
            'data-test': 'add-accounts-button',
            onClick: () => handleAddMembersToOrganization({
                t,
                userState,
                organizationId,
                onReloadOrganization,
                onReloadTable: () => {
                    onReloadTable();
                    invalidateState();
                },
                settings,
            }),
        });
    }
    const appliedStorageFilters = (_e = (_d = (_c = dataTableProps.customBackendResponseData) === null || _c === void 0 ? void 0 : _c.storageClientRequest) === null || _d === void 0 ? void 0 : _d.filter) !== null && _e !== void 0 ? _e : [];
    if (searchParams.get(showAccountsIManageFilterName) === 'true') {
        appliedStorageFilters.push(...getAccountsManageFilter(userState));
    }
    if (hasManageMembersPermission) {
        localActions.push({
            label: t('forms.orgMembers.removeAccounts'),
            icon: React.createElement(DeleteOutlineIcon, null),
            'data-test': 'remove-accounts-button',
            onClick: () => handleRemoveMembersFromOrganization({
                t,
                selectedRowIds: dataTableProps.selectedRowIds,
                appliedStorageFilters,
                rowCount,
                organizationId,
                onReloadOrganization,
                onReloadTable: () => {
                    onReloadTable();
                    invalidateState();
                },
                onDeselectAllRows: dataTableProps.onDeselectAllRows,
            }),
        });
    }
    if (hasManageMembersPermission) {
        localActions.push({
            label: t('forms.orgMembers.changeAccountStatus'),
            icon: React.createElement(PublishedWithChangesIcon, null),
            'data-test': 'toolbar-change-status-button',
            onClick: () => {
                var _a, _b;
                return showChangeOrganizationMemberStatusDialog({
                    organizationId,
                    selectedAccountIds: (_a = dataTableProps.selectedRowIds) !== null && _a !== void 0 ? _a : [],
                    filteredRowCount: (_b = dataTableProps.filteredRowCount) !== null && _b !== void 0 ? _b : 0,
                    appliedStorageFilters,
                    defaultRole: getDefaultOrgRole(orgRoleMap),
                    onAfterSave: () => {
                        var _a;
                        onReloadTable();
                        (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                        invalidateState();
                    },
                });
            },
        }, {
            label: t('forms.orgMembers.changeRole'),
            icon: React.createElement(SocialDistanceIcon, null),
            'data-test': 'toolbar-change-role-button',
            onClick: () => {
                var _a, _b;
                showChangeOrganizationMemberRoleDialog({
                    organizationId,
                    selectedAccountIds: (_a = dataTableProps.selectedRowIds) !== null && _a !== void 0 ? _a : [],
                    filteredRowCount: (_b = dataTableProps.filteredRowCount) !== null && _b !== void 0 ? _b : 0,
                    appliedStorageFilters,
                    onAfterSave: () => {
                        var _a;
                        onReloadTable();
                        (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                    },
                });
            },
        });
    }
    if (hasExportPermission) {
        localActions.push({
            label: t('panels.accounts.export'),
            icon: React.createElement(FileUploadOutlinedIcon, null),
            'data-test': 'toolbar-export-button',
            onClick: () => handleExportAccounts(exportClient, accountsExportFormat, dataTableProps, appliedStorageFilters),
        });
    }
    if (allowCreateGroup(userState)) {
        localActions.push({
            label: t('panels.accounts.addToGroup'),
            icon: React.createElement(GroupAddOutlinedIcon, null),
            disabled: rowCount === 0,
            'data-test': 'toolbar-add-to-group-button',
            menuItems: [
                {
                    label: t('panels.accounts.newGroup'),
                    'data-test': 'new-group-button',
                    onClick: () => handleAddToNewGroup({
                        t,
                        selectedRowIds: dataTableProps.selectedRowIds,
                        appliedStorageFilters,
                        rowCount,
                        groupStorageClient,
                        onDeselectAllRows: dataTableProps.onDeselectAllRows,
                    }),
                },
                {
                    label: t('panels.accounts.addToExistingGroup'),
                    'data-test': 'existing-group-button',
                    onClick: () => handleAddToExistingGroup({
                        t,
                        selectedRowIds: dataTableProps.selectedRowIds,
                        appliedStorageFilters,
                        onDeselectAllRows: dataTableProps.onDeselectAllRows,
                    }),
                },
            ],
        });
    }
    return {
        globalActions,
        localActions,
    };
}
